import React from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';
import Pagination from '../../../components/Pagination.jsx';

function formatPrice(cents) {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

function StatusBadge({ deal }) {
  const [label, classes] =
    deal.status === 'redeemed' ? ['Redeemed', 'bg-gray-100 text-gray-700 border-gray-200']
    : deal.status === 'voided' ? ['Withdrawn', 'bg-red-50 text-red-700 border-red-200']
    : deal.expired ? ['Expired', 'bg-amber-50 text-amber-800 border-amber-200']
    : ['Active', 'bg-green-50 text-green-700 border-green-200'];

  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${classes}`}>{label}</span>;
}

export default function SuperadminDealsIndex({ deals = [], filters = {}, authors = [], pagination }) {
  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Superadmin
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Deals</h1>
            <p className="mt-1 text-sm text-gray-500">
              Pre-priced offers shared as a link. The customer cannot change the licenses, quantities, or price.
            </p>
          </div>
          <Link href="/superadmin/deals/new"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
            </svg>
            New deal
          </Link>
        </div>

        {authors.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <label htmlFor="author" className="text-sm font-medium text-gray-600">Created by</label>
            <select
              id="author"
              value={filters.author || ''}
              onChange={(e) => router.get('/superadmin/deals', e.target.value ? { author: e.target.value } : {},
                                          { preserveState: true, preserveScroll: true })}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm"
            >
              <option value="">Everyone</option>
              {authors.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
            {filters.author && (
              <button
                type="button"
                onClick={() => router.get('/superadmin/deals', {}, { preserveState: true, preserveScroll: true })}
                className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          {deals.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No deals yet.</p>
              <Link href="/superadmin/deals/new" className="mt-3 inline-flex text-sm font-semibold text-indigo-600 hover:text-indigo-700">
                Create the first one →
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {deals.map((deal) => (
                <div key={deal.id} className="flex flex-col sm:flex-row sm:items-start sm:justify-between py-4 gap-3 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link href={`/superadmin/deals/${deal.id}`} className="font-semibold text-gray-900 hover:text-indigo-700">
                        {deal.name}
                      </Link>
                      <StatusBadge deal={deal} />
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                        {deal.plan === 'subscription' ? 'Subscription' : 'One-time'}
                      </span>
                      {deal.target_facility ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {deal.target_facility.name}
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                          Open link
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-600">
                      {deal.total_seats} seat{deal.total_seats === 1 ? '' : 's'} ·{' '}
                      <span className="font-semibold text-indigo-700">{formatPrice(deal.price_cents)}</span>
                      {deal.list_subtotal_cents > deal.price_cents && (
                        <> <span className="line-through text-gray-400">{formatPrice(deal.list_subtotal_cents)}</span></>
                      )}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      Created {deal.created_at}
                      {deal.author_name && ` by ${deal.author_name}`}
                      {deal.redeemed_at && ` · redeemed ${deal.redeemed_at}`}
                    </p>
                  </div>
                  <Link href={`/superadmin/deals/${deal.id}`}
                        className="shrink-0 inline-flex items-center justify-center px-3 py-2 text-sm font-semibold rounded-lg text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition">
                    View
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>

        {pagination && (
          <Pagination
            {...pagination}
            path="/superadmin/deals"
            label="deals"
            params={filters.author ? { author: filters.author } : {}}
          />
        )}
      </div>
    </Layout>
  );
}
